/* global gtag */
import React, { useState } from "react";
import {
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Tooltip,
  Paper,
  Button,
  Chip,
  LinearProgress,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalendarTodayIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOnOutlined";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import dayjs from "dayjs";

const JobCard = ({
  job,
  index,
  logoImage,
  onEdit,
  onDelete,
  onViewApplication,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    onEdit(index);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    onDelete(index);
    handleMenuClose();
  };

  const formatDate = (date) => {
    return date
      ? dayjs(date).format("M/D/YYYY")
      : dayjs().format("M/D/YYYY");
  };

  // Enhanced check for empty values
  const isEmptyValue = (value) => {
    return value === null || value === undefined || value === "" || (typeof value === 'string' && value.trim() === "");
  };

  const getCompanyName = (name) => {
    return !isEmptyValue(name) ? name : "Company Name";
  };

  const getJobTitle = (title) => {
    return !isEmptyValue(title) ? title : "Job Position";
  };

  const getDescription = (desc) => {
    return !isEmptyValue(desc) ? desc : "No description available for this position.";
  };

  const getLocation = (location) => {
    return !isEmptyValue(location) ? location : "Remote";
  };

  // The API has never returned a salary. This used to fall back to a hardcoded
  // "$80k - $120k", i.e. it displayed a made-up range on every card. Show nothing
  // instead — no number beats a wrong one.
  const getSalary = () => {
    const salaryFields = [
      job.salary,
      job.Salary,
      job["Salary Range"],
      job["salary_range"],
      job.compensation
    ];

    for (let salary of salaryFields) {
      if (!isEmptyValue(salary)) {
        return salary;
      }
    }
    return null;
  };

  // `candidate_count` is the real field. The previous fallback chain probed four key
  // spellings the API never returned and then made up a number with
  // Math.floor(Math.random() * 50) + 10 — so every card showed a random applicant count.
  const getApplicantsCount = () => {
    const count = job.candidate_count;
    return count === null || count === undefined ? null : count;
  };

  const getJobType = () => {
    const typeFields = [
      job["Job Type"],
      job.jobType,
      job.type,
      job.employment_type
    ];
    
    for (let type of typeFields) {
      if (!isEmptyValue(type)) {
        return type;
      }
    }
    return "Full-time";
  };

  const getStatus = () => {
    const statusFields = [
      job.status,
      job.Status,
      job["Job Status"]
    ];
    
    for (let status of statusFields) {
      if (!isEmptyValue(status)) {
        return status;
      }
    }
    return "Active"; // Default to Active
  };

  const applicantsCount = getApplicantsCount();

  const handleAnalytics = (eventLabel) => {
    if (typeof gtag === "function") {
      gtag("event", "button_click", {
        event_category: "User Interaction",
        event_label: eventLabel,
      });
    }
  };

  return (
    <Paper
      sx={{
        padding: "24px",
        borderRadius: "16px",
        backgroundColor: "#fff",
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.15)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Header with Logo, Title, and Menu */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
          gap: "12px",
        }}
      >
        {/* Company Logo */}
        <Box
          sx={{
            width: "48px",
            height: "48px",
            borderRadius: "16px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BusinessCenterIcon
            sx={{
              color: "white",
              fontSize: "24px",
            }}
          />
        </Box>

        {/* Title and Company */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Raleway",
              fontSize: "18px",
              fontWeight: 600,
              color: "#1f2937",
              lineHeight: "22px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginBottom: "2px",
            }}
          >
            {getJobTitle(job["Job Title"])}
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "Raleway",
              fontSize: "14px",
              fontWeight: 400,
              color: "#6b7280",
              lineHeight: "18px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getCompanyName(job["Company Name"])}
          </Typography>
        </Box>

        {/* Status Badge and Menu */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <Chip
            label={getStatus()}
            size="small"
            sx={{
              backgroundColor: getStatus() === "Active" ? "#dcfce7" : "#fee2e2",
              color: getStatus() === "Active" ? "#16a34a" : "#dc2626",
              fontWeight: 500,
              fontSize: "12px",
              height: "24px",
              "& .MuiChip-label": {
                display: "flex",
                alignItems: "center",
                gap: "4px",
                paddingX: "8px",
              },
            }}
            icon={
              <Box
                sx={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: getStatus() === "Active" ? "#16a34a" : "#dc2626",
                }}
              />
            }
          />
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{ 
              padding: "4px",
              width: "32px",
              height: "32px",
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEditClick}>Edit</MenuItem>
          <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
        </Menu>
      </Box>

      {/* Job Description */}
      <Typography
        sx={{
          fontFamily: "Raleway",
          fontSize: "14px",
          lineHeight: "20px",
          color: "#4b5563",
          marginBottom: "16px",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {getDescription(job.description)}
      </Typography>

      {/* Date and Location - Key Value Format */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        {/* Date Row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#374151",
              fontFamily: "Raleway",
            }}
          >
            Posted Date:
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#6b7280",
              fontFamily: "Raleway",
            }}
          >
            {formatDate(job["Posting Date"])}
          </Typography>
        </Box>

        {/* Location Row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#374151",
              fontFamily: "Raleway",
            }}
          >
            Location:
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#6b7280",
              fontFamily: "Raleway",
            }}
          >
            {getLocation(job.Location)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#374151",
              fontFamily: "Raleway",
            }}
          >
           Job Type:
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#6b7280",
              fontFamily: "Raleway",
            }}
          >
            {getJobType()}
          </Typography>
        </Box>
      </Box>

      {/* Job Type and Applicants */}
      {/* <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      > */}
        {/* <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <PeopleIcon sx={{ fontSize: "16px", color: "#9ca3af" }} />
          <Typography
            sx={{
              fontSize: "14px",
              color: "#6b7280",
              fontFamily: "Raleway",
            }}
          >
            {applicantsCount} applicants
          </Typography>
        </Box> */}
{/* 
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <AccessTimeIcon sx={{ fontSize: "16px", color: "#9ca3af" }} />
          <Typography
            sx={{
              fontSize: "14px",
              color: "#6b7280",
              fontFamily: "Raleway",
            }}
          >
            {getJobType()}
          </Typography>
        </Box> */}
      {/* </Box> */}

      {/* Action Buttons */}
      <Box sx={{ marginTop: "auto", display: "flex", gap: "12px", justifyContent: "space-between" }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            handleAnalytics("View Applications");
            onViewApplication(job);
          }}
          sx={{ textTransform: "none" }}
        >
          View Application
        </Button>
        
        <Button
          variant="outlined"
          onClick={handleEditClick}
          size="small"
          sx={{ textTransform: "none" }}
        >
          Edit
        </Button>
      </Box>
    </Paper>
  );
};

export default JobCard;